import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "@tanstack/react-router";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { getPointsBadge, getTeamLogo } from "@/lib/utils";

export type PeriodTableRow = {
    playerId: number;
    teamId: number;
    playerName: string | null;
    playerLastName: string | null;
    playerRole: string | null;
    pointsDefensive: number;
    pointsOffensive: number;
    pointsTeamPlay: number;
    pointsEffort: number;
    pointsBonus: number;
    totalPoints: number;
};

export type PeriodTableRowWithTeam = PeriodTableRow & { teamName: string };

export const periodTableColumns: ColumnDef<PeriodTableRow>[] = [
    {
        id: "rank",
        header: "Rank",
        size: 72,
        meta: { shortHeader: "#", label: "Rank" },
        cell: ({ row, table }) => {
            const allData = table.getCoreRowModel().rows;
            const sortedData = [...allData].sort((a, b) => b.original.totalPoints - a.original.totalPoints);
            const rank = sortedData.findIndex((r) => r.id === row.id) + 1;
            return (
                <span
                    className={`font-headline font-bold text-xs ${rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"}`}
                >
                    {`#${rank.toString().padStart(2, "0")}`}
                </span>
            );
        },
    },
    {
        id: "player",
        header: "Player name",
        size: 155,
        meta: { shortHeader: "Player", label: "Player Name" },
        cell: ({ row }) => (
            <Link
                to="/player/$playerId"
                params={{ playerId: row.original.playerId.toString() }}
                search={{ teamId: row.original.teamId }}
                className="font-bold text-on-surface hover:text-primary hover:underline transition-colors"
            >
                {row.original.playerName} {row.original.playerLastName}
            </Link>
        ),
    },
    {
        accessorKey: "playerRole",
        header: "Role",
        size: 100,
        meta: { shortHeader: "Pos", label: "Role" },
        cell: ({ getValue }) => {
            const role = getValue() as string | null;
            return (
                <span className="px-2 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant">
                    {role ?? "N/A"}
                </span>
            );
        },
    },
    {
        accessorKey: "pointsDefensive",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Defensive</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Defensive performance metrics covering pressure application, save percentage, puck battles, shot
                        blocking, and clearances.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        meta: {
            shortHeader: "Def",
            label: "Defensive",
            description:
                "Defensive performance metrics covering pressure application, save percentage, puck battles, shot blocking, and clearances.",
        },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span>{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsOffensive",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Offensive</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Attacking performance metrics including shooting accuracy, passing ability, goal creation, and
                        puck handling skills.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        meta: {
            shortHeader: "Off",
            label: "Offensive",
            description:
                "Attacking performance metrics including shooting accuracy, passing ability, goal creation, and puck handling skills.",
        },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span>{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsTeamPlay",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Team Play</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Team contribution metrics measuring discipline, plus/minus rating, shot attempt differentials,
                        and puck management.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        meta: {
            shortHeader: "TP",
            label: "Team Play",
            description:
                "Team contribution metrics measuring discipline, plus/minus rating, shot attempt differentials, and puck management.",
        },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span>{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsEffort",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Effort</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Physical performance metrics tracking speed, distance covered, puck handling speed, and movement
                        intensity.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        meta: {
            shortHeader: "Eff",
            label: "Effort",
            description:
                "Physical performance metrics tracking speed, distance covered, puck handling speed, and movement intensity.",
        },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span>{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsBonus",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Bonus</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Additional points awarded for goals and assists scored during the game.</p>
                </TooltipContent>
            </Tooltip>
        ),
        meta: {
            shortHeader: "Bon",
            label: "Bonus",
            description: "Additional points awarded for goals and assists scored during the game.",
        },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span>{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "totalPoints",
        header: "Total Points",
        size: 112,
        meta: { shortHeader: "Total", label: "Total Points" },
        cell: ({ getValue }) => {
            const value = getValue() as number;
            if (value === 0) {
                return (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help text-on-surface-variant">-</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                Player has spent insufficient time on the ice to be objectively evaluated against our
                                KPI scale.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                );
            }
            const { bg, text, border } = getPointsBadge(value);
            return (
                <div
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${bg} ${text} rounded font-bold text-xs border ${border}`}
                >
                    {value}
                </div>
            );
        },
    },
];

export const teamLogoColumn: ColumnDef<PeriodTableRowWithTeam> = {
    id: "team",
    header: "Team",
    meta: { shortHeader: "Tm", label: "Team" },
    cell: ({ row }) => {
        const teamName = row.original.teamName;
        const logo = getTeamLogo(teamName);
        return logo ? (
            <img src={logo} alt={teamName} className="h-5 w-auto object-contain" title={teamName} />
        ) : (
            <span className="text-[9px] font-bold uppercase text-on-surface-variant">{teamName.slice(0, 3)}</span>
        );
    },
};

export const periodCombinedTableColumns: ColumnDef<PeriodTableRowWithTeam>[] = [
    periodTableColumns[0] as unknown as ColumnDef<PeriodTableRowWithTeam>,
    teamLogoColumn,
    ...(periodTableColumns.slice(1) as unknown as ColumnDef<PeriodTableRowWithTeam>[]),
];
